@file:Suppress("UnsafeCastFromDynamic")

package com.zi.support.web

import com.zi.support.shared.RecentSearch

import com.zi.support.shared.CallDay
import com.zi.support.shared.CallRank
import com.zi.support.shared.CallStatDay
import com.zi.support.shared.CallLogPolicy
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import kotlin.js.Date

/**
 * Top-bar phone button > Calls: every call in supportCalls with the activity graph and history
 * (Call Log App parity). Browsers can't read a phone's call log, so calls are captured by the
 * Android app; the web views them, creates tickets from them and can mark them personal.
 */
internal object CallsScreen {
    private var stopHandle: dynamic = null
    private var badgeStop: dynamic = null
    private var badgeUid = ""
    /** Newest [CallLogPolicy.LIST_LIMIT] calls in the chosen scope. */
    private var calls: List<WebSupportCall> = emptyList()
    /** Daily totals for the graph and insights (callStats), covering every call. */
    private var statDays: List<CallStatDay> = emptyList()
    /** Server results while searching or filtering; null shows the newest calls. */
    private var found: List<WebSupportCall>? = null
    private var statsStop: dynamic = null
    private val searchStops = mutableListOf<dynamic>()
    private var searchTimer = 0
    /** Every call from the last RecentSearch.DAYS days, loaded the first time someone searches. */
    private var recentCalls: List<WebSupportCall>? = null
    private var loaded = false
    private var mineOnly = false
    private var monthRange = false
    private var filter = "All"
    private var search = ""
    private var selectedDay = -1
    private var page = 0
    /** Insights animate in when shown and when the range or scope changes, not on every live update. */
    private var animateInsights = true
    /** The Insights toggle: the week or month as charts instead of the day chart and history. */
    private var insightsView = false
    private val insightRange = Infographic.Range()
    /** True until callStats for the current scope and period arrive, so insights never show the previous load. */
    private var statsLoading = true
    /** How many days back the callStats listener reads: the usual window, or further for an older custom period. */
    private var statsBack = 0
    private const val PAGE_SIZE = 30
    private const val DAY = 86_400_000.0
    private val uid get() = FirebaseGateway.currentUser()?.uid as? String ?: ""

    fun setPersonal(callId: String, personal: Boolean) = promise(FirebaseGateway.update("${CallLogPolicy.COLLECTION}/$callId", json(
        "personal" to personal, "personalMarkedAt" to FirebaseGateway.serverTimestamp(), "updatedAt" to FirebaseGateway.serverTimestamp()
    )), { toast(if (personal) "Marked as personal." else "No longer personal.") })

    /** Hides the number and all its calls, now and in future; the server does the writes. */
    fun markSpam(callId: String) = promise(FirebaseGateway.callable("markCallerSpam", json("callId" to callId)), { toast("Marked as spam. Calls from this number are hidden.") })

    /** Keeps the top-bar badge in sync: the team's calls that still need a ticket, the same on every device. */
    fun watchBadge() {
        val current = uid
        if (current.isBlank() || (badgeStop != null && badgeUid == current)) { paintBadge(); return }
        cancelListener(badgeStop); badgeUid = current
        val fs = FirebaseGateway.firestore
        // Last 30 days only (linkedTicketId + startedAt index): calls that never got a ticket aren't re-read forever.
        val since = FirebaseGateway.timestampFromMillis((js("Date.now()") as Double) - CallLogPolicy.BADGE_WINDOW_MILLIS)
        badgeStop = FirebaseGateway.listenQuery(
            fs.query(FirebaseGateway.collection(CallLogPolicy.COLLECTION), fs.where("linkedTicketId", "==", null), fs.where("startedAt", ">=", since), fs.orderBy("startedAt", "desc")),  // newest-first so it uses the existing (linkedTicketId, startedAt DESC) index
            { rows -> badgeCount = rows.count { it.data.personal != true && it.data.spam != true }; paintBadge() }, {})
    }

    private var badgeCount = 0
    private fun paintBadge() {
        val badge = element("calls-badge") ?: return
        badge.textContent = if (badgeCount > 99) "99+" else badgeCount.toString()
        badge.style.display = if (badgeCount > 0) "grid" else "none"
        element("calls-button")?.setAttribute("aria-label", if (badgeCount > 0) "Calls, $badgeCount need a ticket" else "Calls")
    }

    fun stop() {
        cancelListener(stopHandle); stopHandle = null; cancelListener(statsStop); statsStop = null
        searchStops.forEach { cancelListener(it) }; searchStops.clear(); kotlinx.browser.window.clearTimeout(searchTimer)
        loaded = false
    }

    fun render() {
        if (MobileInsights.enabled) { insightsView = true; mineOnly = MobileInsights.filter("scope") == "mine" }
        stop()
        selectedDay = -1
        animateInsights = true
        element("content")?.innerHTML = "<div id=\"calls-root\"><div class=\"empty\">Loading calls…</div></div>"
        listen()
    }

    /** The newest calls and the daily totals for the chosen scope (everyone or mine). */
    private fun listen() {
        cancelListener(stopHandle); cancelListener(statsStop)
        val fs = FirebaseGateway.firestore
        val base = FirebaseGateway.collection(CallLogPolicy.COLLECTION)
        val newest = if (mineOnly) fs.query(base, fs.where("receivedByUid", "==", uid), fs.orderBy("startedAt", "desc"), fs.limit(CallLogPolicy.LIST_LIMIT))
            else fs.query(base, fs.orderBy("startedAt", "desc"), fs.limit(CallLogPolicy.LIST_LIMIT))
        stopHandle = FirebaseGateway.listenQuery(newest,
            { rows -> show(rows.map { r -> WebSupportCall.from(json("id" to r.id, "data" to { r.data })) }) },
            { element("calls-root")?.innerHTML = "<div class=\"empty\">${escape(it)}</div>" })
        val now = Date().getTime()
        statsLoading = true
        statsBack = maxOf(CallLogPolicy.STATS_DAYS - 1, insightRange.first + insightRange.days)  // + the period before, for the trend
        statsStop = FirebaseGateway.listenQuery(
            fs.query(FirebaseGateway.collection("callStats/${if (mineOnly) uid else "team"}/days"), fs.where("day", ">=", dayKey(now - statsBack * DAY))),
            { rows -> val today = dayKey(Date().getTime()); statDays = rows.map { statDay(it.data, today) }; statsLoading = false; if (loaded) { drawSummary(); drawInsights(); drawHead() } },
            { toast(it, true) })
        searchCalls()
    }

    internal fun show(rows: List<WebSupportCall>) { calls = rows.filterNot { it.spam }; loaded = true; draw() }

    /** Searching or filtering asks the server, so older calls show too; the newest loaded calls that contain the text are added. */
    private fun searchCalls() {
        searchStops.forEach { cancelListener(it) }; searchStops.clear()
        val term = search.trim()
        if (term.isBlank() && filter == "All") { found = null; drawList(); return }
        val fs = FirebaseGateway.firestore
        val base = FirebaseGateway.collection(CallLogPolicy.COLLECTION)
        val queries = mutableListOf<dynamic>()
        if (term.isNotBlank()) {
            // Search: every call from the last 90 days (loaded once) plus the live list, every word matched anywhere.
            if (recentCalls == null) {
                recentCalls = emptyList()
                val since = FirebaseGateway.timestampFromMillis(RecentSearch.sinceMillis(kotlin.js.Date.now()))
                promise(fs.getDocs(fs.query(base, fs.where("startedAt", ">=", since), fs.orderBy("startedAt", "desc"), fs.limit(RecentSearch.LIMIT))), { snapshot: dynamic ->
                    val docs: dynamic = snapshot.docs
                    recentCalls = (0 until (docs.length as Int)).map { WebSupportCall.from(docs[it]) }
                    if (search.isNotBlank()) searchCalls()
                }, { message -> recentCalls = null; toast(message, true) })
            }
            val since = RecentSearch.sinceMillis(kotlin.js.Date.now())
            found = (recentCalls.orEmpty() + calls).distinctBy { it.id }.filter { call ->
                !call.spam && call.startedAtMillis >= since && (!mineOnly || call.receivedByUid == uid) &&
                    when (filter) { "Needs ticket" -> call.needsTicket; "Missed" -> call.outcome in CallLogPolicy.MISSED_OUTCOMES; else -> true } &&
                    RecentSearch.matches(term, listOf(call.callerName, call.callerPhone, call.receivedByName, call.deviceName, call.linkedTicketNumber, call.outcome, call.direction))
            }.sortedByDescending { it.startedAtMillis }
            drawList(); return
        } else if (filter == "Needs ticket") {
            queries.add(if (mineOnly) fs.query(base, fs.where("receivedByUid", "==", uid), fs.where("linkedTicketId", "==", null), fs.orderBy("startedAt", "desc"), fs.limit(CallLogPolicy.FILTER_LIMIT))
                else fs.query(base, fs.where("linkedTicketId", "==", null), fs.orderBy("startedAt", "desc"), fs.limit(CallLogPolicy.FILTER_LIMIT)))
        } else {
            queries.add(fs.query(base, fs.where("outcome", "in", CallLogPolicy.MISSED_OUTCOMES.toTypedArray()), fs.orderBy("startedAt", "desc"), fs.limit(CallLogPolicy.FILTER_LIMIT)))
        }
        val results = arrayOfNulls<List<WebSupportCall>>(queries.size)
        fun merge() {
            found = (results.filterNotNull().flatten() + calls).filterNot { it.spam }.distinctBy { it.id }.sortedByDescending { it.startedAtMillis }
            drawList()
        }
        queries.forEachIndexed { i, query ->
            searchStops.add(FirebaseGateway.listenQuery(query, { rows ->
                results[i] = rows.map { r -> WebSupportCall.from(json("id" to r.id, "data" to { r.data })) }; merge()
            }, { toast(it, true) }))
        }
        merge()
    }

    private fun statDay(data: dynamic, today: String): CallStatDay {
        fun n(value: dynamic) = (value as? Number)?.toInt() ?: 0
        fun ranks(map: dynamic): List<CallRank> = if (map == null) emptyList() else (js("Object").keys(map) as Array<String>).map { CallRank(it, n(map[it]?.count), n(map[it]?.answered)) }
        val hours: dynamic = data.hours
        val day = field(data, "day").ifBlank { today }
        return CallStatDay(kotlin.math.round((Date.parse(today) - Date.parse(day)) / DAY).toInt(), n(data.answered), n(data.missed),
            ((data.talkSeconds as? Number)?.toLong()) ?: 0L, n(data.talkCalls), n(data.incoming), n(data.outgoing), n(data.linked), n(data.personal),
            List(24) { if (hours == null) 0 else n(hours[it.toString()]) }, ranks(data.callers), ranks(data.team))
    }

    /** yyyy-MM-dd in South African time (UTC+2), the same days the tallyCallStats function counts in. */
    private fun dayKey(millis: Double): String = (Date(millis + 2 * 3600_000.0).toISOString()).take(10)

    private fun drawHead() {
        val month = CallLogPolicy.insightsFromStats(statDays, 30)
        element("calls-head-sub")?.innerHTML = "${month.total} calls in 30 days · ${if (month.needsTicket == 0) "all have tickets" else "<strong>${month.needsTicket}</strong> need a ticket"}"
    }

    private fun draw() {
        if (!loaded) return
        val root = element("calls-root") ?: return
        root.innerHTML = """
          <div class="calls-head"><div><h2>Calls</h2><p id="calls-head-sub"></p></div><button id="calls-insights-toggle" class="btn tk-insights-toggle" aria-pressed="$insightsView">${if (insightsView) "<span class=\"material-symbols-rounded\" aria-hidden=\"true\">arrow_back</span><span>Back to calls</span>" else "<span class=\"material-symbols-rounded\" aria-hidden=\"true\">bar_chart</span><span>Insights</span>"}</button></div>
          <p class="page-note"${if (insightsView) " hidden" else ""}><span class="material-symbols-rounded">info</span>Calls are captured by the SupportApp Android app on each support phone. Browsers and iPhones can't read a phone's call log.</p>
          <div id="calls-insights"${if (insightsView) "" else " hidden"}></div>
          <div class="calls-layout"${if (insightsView) " hidden" else ""}><section id="calls-summary" class="calls-card"></section><section class="calls-history"><div id="calls-history-head"></div><div id="calls-list"></div></section></div>
        """.trimIndent()
        click("calls-insights-toggle") { insightsView = !insightsView; animateInsights = true; draw() }
        drawHead(); drawSummary(); drawInsights(); drawHistoryHead(); drawList()
    }

    // --- Activity graph (Call Log App SummaryCard) --------------------------------------------

    private fun drawSummary() {
        val range = if (monthRange) 30 else 7
        val stats = CallLogPolicy.dailyFromStats(statDays, range + 1) // +1: the day before, for comparison
        val shown = stats.drop(1)
        if (selectedDay !in shown.indices) selectedDay = shown.lastIndex
        val day = shown[selectedDay]; val previous = stats[selectedDay]
        val delta = day.total - previous.total
        val vs = dayName(previous.daysAgo, "short")
        val (deltaText, deltaClass) = when { delta > 0 -> "▲ $delta vs $vs" to "up"; delta < 0 -> "▼ ${-delta} vs $vs" to "down"; else -> "Same as $vs" to "" }
        element("calls-summary")?.innerHTML = """
          <div class="calls-card-top">${segmented("calls-scope", listOf("Everyone", "Mine"), if (mineOnly) 1 else 0)}${segmented("calls-range", listOf("Week", "Month"), if (monthRange) 1 else 0)}</div>
          <p class="calls-day">${escape(dayLabel(day.daysAgo))}</p>
          <div class="calls-total"><strong>${day.total}</strong><span>${if (day.total == 1) "call" else "calls"}</span><em class="calls-delta $deltaClass">${escape(deltaText)}</em></div>
          ${chart(shown, selectedDay)}
          <div class="calls-stats">${stat("Answered", day.answered.toString(), "answered")}${stat("Missed", day.missed.toString(), "missed")}${stat("Avg talk", day.averageTalkSeconds?.let(CallLogPolicy::shortDuration) ?: "–", "")}</div>
        """.trimIndent()
        bindSegmented("calls-scope") { mineOnly = it == 1; selectedDay = -1; page = 0; animateInsights = true; statDays = emptyList(); drawSummary(); drawInsights(); listen() }
        bindSegmented("calls-range") { monthRange = it == 1; selectedDay = -1; animateInsights = true; drawSummary(); drawInsights() }
        document.querySelectorAll("[data-call-day]").let { nodes ->
            for (i in 0 until nodes.length) {
                val node = nodes.item(i) as HTMLElement
                val pick = { val next = node.getAttribute("data-call-day")!!.toInt(); if (next != selectedDay) { selectedDay = next; drawSummary() } }
                node.addEventListener("click", { pick() })
                node.addEventListener("mouseenter", { e -> if ((e.asDynamic().buttons as Int) == 1) pick() })
                node.addEventListener("keydown", { e -> if (e.asDynamic().key == "Enter") pick() })
            }
        }
    }

    // --- Insights: the whole week or month at a glance -----------------------------------------

    private fun drawInsights() {
        val root = element("calls-insights") ?: return
        if (!insightsView) { root.innerHTML = ""; return }
        if (statsLoading) {
            root.innerHTML = Infographic.loading(insightRange, segmented("ins-calls-scope", listOf("Everyone", "Mine"), if (mineOnly) 1 else 0))
            bindSegmented("ins-calls-scope") { mineOnly = it == 1; selectedDay = -1; page = 0; animateInsights = true; statDays = emptyList(); drawInsights(); listen() }
            Infographic.bindRange(root, insightRange) { animateInsights = true; if (insightRange.first + insightRange.days > statsBack) listen() else drawInsights() }
            return
        }
        val range = insightRange.days
        // Shift so the period's last day reads as "today": the policy counts back from day 0.
        val shifted = statDays.map { it.copy(daysAgo = it.daysAgo - insightRange.last) }
        val s = CallLogPolicy.insightsFromStats(shifted, range)
        val days = CallLogPolicy.dailyFromStats(shifted, range)
        val (delta, trend) = Infographic.trend(s.total, s.previousTotal, insightRange.noun())
        fun hour(h: Int) = if (h < 10) "0$h:00" else "$h:00"
        val labels = insightRange.dayLabels()
        // Weekday × hour from each day's hourly tallies.
        val week = List(7) { IntArray(24) }.also { grid ->
            statDays.filter { it.daysAgo in insightRange.last..insightRange.first }.forEach { d ->
                val weekday = (kotlin.js.Date(Infographic.midnight(d.daysAgo)).getDay() + 6) % 7
                d.byHour.forEachIndexed { h, n -> if (h < 24) grid[weekday][h] += n }
            }
        }.map { it.toList() }
        val controls = "<div class=\"ins-controls\">${Infographic.EXPORT_BUTTON}${segmented("ins-calls-scope", listOf("Everyone", "Mine"), if (mineOnly) 1 else 0)}${Infographic.rangeControls(insightRange)}</div>"
        val body = Infographic.figures(listOf(
                Infographic.Figure("Calls", "${s.total}", delta, trend, count = s.total, spark = days.map { it.answered + it.missed }),
                Infographic.Figure("Answer rate", "${s.answerRate}%", "${s.answered} answered · ${s.missed} missed"),
                Infographic.Figure("Need a ticket", "${s.needsTicket}", if (s.needsTicket > 0) "Not linked yet" else "All covered", if (s.needsTicket > 0) "down" else "up", count = s.needsTicket),
                Infographic.Figure("Linked to tickets", "${s.linked}", "${Infographic.percent(s.linked, s.total)}% of calls", count = s.linked),
                Infographic.Figure("Talk time", if (s.talkSeconds > 0) CallLogPolicy.shortDuration(s.talkSeconds) else "–", s.averageTalkSeconds?.let { "Avg ${CallLogPolicy.shortDuration(it)} per call" } ?: ""),
                Infographic.Figure("Unique callers", "${s.uniqueCallers}", s.busiestHour?.let { "Busiest ${hour(it)}" } ?: "", count = s.uniqueCallers))) +
            (if (range < 2) "" else Infographic.line("Calls per day", insightRange.label(), labels, listOf(
                Infographic.Series("Answered", "#0d9488", days.map { it.answered }),
                Infographic.Series("Missed", "#dc2626", days.map { it.missed })))) +
            Infographic.donut("Ticket coverage", "Calls in the period", listOf(
                Infographic.Part("Linked", s.linked, "#0d9488"), Infographic.Part("Personal", s.personal, "#64748b"), Infographic.Part("Need a ticket", s.needsTicket, "#d97706")), "calls") +
            Infographic.heatmap("When calls come in", week, "calls", "#2563eb") +
            Infographic.donut("Direction", "Calls in the period", listOf(Infographic.Part("Incoming", s.incoming, "#2563eb"), Infographic.Part("Outgoing", s.outgoing, "#7c3aed")), "calls") +
            Infographic.table("Top callers", "By number of calls", s.topCallers.map { it.label to it.count }, s.total, "No named or numbered callers yet") +
            (if (mineOnly) "" else Infographic.table("Who took the calls", "Calls per person", s.team.map { it.label to it.count }, s.total, "No calls taken yet"))
        root.innerHTML = controls + Infographic.section("Call insights", "${insightRange.label()} · ${if (mineOnly) "your calls" else "the whole team"}", body, animateInsights)
        bindSegmented("ins-calls-scope") { mineOnly = it == 1; selectedDay = -1; page = 0; animateInsights = true; statDays = emptyList(); drawInsights(); listen() }
        Infographic.bindRange(root, insightRange) { animateInsights = true; if (insightRange.first + insightRange.days > statsBack) { listen(); drawInsights() } else drawInsights() }
        Infographic.bindExport(root, "calls-insights", insightRange) { if (mineOnly) mapOf("scope" to "mine") else emptyMap() }
        (root.querySelector(".ins") as? HTMLElement)?.let(Infographic::bind)
        animateInsights = false
    }

    /** Stacked answered/missed bars in an SVG; each day is a focusable hit area. Bars grow in via CSS. */
    private fun chart(days: List<CallDay>, selected: Int): String {
        val w = 100.0 / days.size
        val max = days.maxOf { it.total }.coerceAtLeast(1)
        val barW = w * (if (days.size > 10) .62 else .5)
        val bars = days.mapIndexed { i, d ->
            val x = i * w + (w - barW) / 2
            val answeredH = 100.0 * d.answered / max
            val missedH = 100.0 * d.missed / max
            val on = if (i == selected) " on" else ""
            val empty = if (d.total == 0) "<rect class=\"calls-bar-empty\" x=\"$x\" y=\"98\" width=\"$barW\" height=\"2\" rx=\"1\"/>" else ""
            val answered = if (answeredH > 0) "<rect class=\"calls-bar answered$on\" x=\"$x\" y=\"${100 - answeredH}\" width=\"$barW\" height=\"$answeredH\" rx=\"1.2\"/>" else ""
            val missed = if (missedH > 0) "<rect class=\"calls-bar missed$on\" x=\"$x\" y=\"${100 - answeredH - missedH - if (answeredH > 0) 1.5 else 0.0}\" width=\"$barW\" height=\"$missedH\" rx=\"1.2\"/>" else ""
            "$empty$answered$missed"
        }.joinToString("")
        val labels = days.mapIndexed { i, d ->
            val show = days.size <= 10 || i == selected || i == days.lastIndex || (days.lastIndex - i) % 5 == 0
            val text = if (days.size <= 10) dayName(d.daysAgo, "narrow") else dayOfMonth(d.daysAgo)
            "<button type=\"button\" class=\"calls-hit${if (i == selected) " on" else ""}\" data-call-day=\"$i\" aria-label=\"${escape(dayLabel(d.daysAgo))}: ${d.total} calls\"><span>${if (show) escape(text) else ""}</span></button>"
        }.joinToString("")
        val bubbleLeft = (selected + .5) * w
        val bubbleBottom = 100.0 * days[selected].total / max
        return """
          <div class="calls-chart" style="--n:${days.size}">
            <div class="calls-plot"><span class="calls-highlight" style="left:${selected * w}%;width:$w%"></span>
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">$bars</svg>
              <span class="calls-bubble" style="left:$bubbleLeft%;bottom:$bubbleBottom%">${days[selected].total}</span></div>
            <div class="calls-hits">$labels</div>
          </div>
        """.trimIndent()
    }

    private fun stat(label: String, value: String, dot: String) =
        "<div><strong>${escape(value)}</strong><span>${if (dot.isBlank()) "" else "<i class=\"calls-dot $dot\"></i>"}${escape(label)}</span></div>"

    // --- History ------------------------------------------------------------------------------

    private fun drawHistoryHead() {
        element("calls-history-head")?.innerHTML = """
          <div class="calls-history-top"><h3>History</h3>${segmented("calls-filter", listOf("All", "Needs ticket", "Missed"), listOf("All", "Needs ticket", "Missed").indexOf(filter))}</div>
          <div class="search-shell"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="calls-search">Search calls</label><input id="calls-search" class="input search" placeholder="Search number or who took it" value="${escape(search)}"></div>
        """.trimIndent()
        bindSegmented("calls-filter") { filter = listOf("All", "Needs ticket", "Missed")[it]; page = 0; drawHistoryHead(); searchCalls() }
        type("calls-search") {
            search = input("calls-search"); page = 0
            // Wait for a pause in typing before asking the server.
            kotlinx.browser.window.clearTimeout(searchTimer); searchTimer = kotlinx.browser.window.setTimeout({ searchCalls() }, 450)
        }
        bindSearchClear("calls-search") { search = ""; page = 0; searchCalls() }
    }

    private fun drawList() {
        val source = found ?: calls
        val visible = source.filter { call ->
            when (filter) { "Needs ticket" -> call.needsTicket; "Missed" -> !CallLogPolicy.isAnswered(call.outcome); else -> true } &&
                (!mineOnly || call.receivedByUid == uid)
        }
        val pages = (visible.size + PAGE_SIZE - 1) / PAGE_SIZE
        page = page.coerceIn(0, maxOf(pages - 1, 0))
        val pager = if (pages > 1) "<div class=\"calls-pager\"><button type=\"button\" class=\"btn\" data-call-page=\"${page - 1}\"${if (page == 0) " disabled" else ""}>‹ Newer</button><span>Page ${page + 1} of $pages</span><button type=\"button\" class=\"btn\" data-call-page=\"${page + 1}\"${if (page >= pages - 1) " disabled" else ""}>Older ›</button></div>" else ""
        element("calls-list")?.innerHTML = if (visible.isEmpty()) "<div class=\"empty\">${if (calls.isEmpty()) "No calls captured yet." else "No calls match."}</div>"
        else visible.drop(page * PAGE_SIZE).take(PAGE_SIZE).groupBy { daysAgo(it.startedAtMillis) }.entries.joinToString("") { (ago, dayCalls) ->
            val heading = if (ago <= 1) dayLabel(ago) else localDate(Date(dayCalls.first().startedAtMillis), js("({weekday:'short',day:'numeric',month:'short',year:'numeric'})"))
            "<h4 class=\"calls-group\">${escape(heading)}</h4><div class=\"calls-group-card\">${dayCalls.joinToString("") { row(it) }}</div>"
        } + pager
        document.querySelectorAll("[data-call-page]").let { nodes ->
            for (i in 0 until nodes.length) nodes.item(i)?.addEventListener("click", { e ->
                page = (e.currentTarget as HTMLElement).getAttribute("data-call-page")!!.toInt(); drawList()
                element("calls-history-head")?.asDynamic()?.scrollIntoView(js("({block:'start'})"))
            })
        }
        document.querySelectorAll("[data-call-row]").let { nodes ->
            for (i in 0 until nodes.length) nodes.item(i)?.addEventListener("click", { e ->
                val id = (e.currentTarget as HTMLElement).getAttribute("data-call-row")
                source.firstOrNull { it.id == id }?.let(::openDetail)
            })
        }
    }

    private fun row(call: WebSupportCall): String {
        val missed = !CallLogPolicy.isAnswered(call.outcome)
        val detail = listOf(if (call.callerName.isBlank()) "" else call.callerPhone, if (call.direction == "outgoing") "Outgoing" else "Incoming",
            if (missed) CallLogPolicy.outcomeLabel(call.outcome) else CallLogPolicy.shortDuration(call.durationSeconds ?: 0),
            call.receivedByName).filter { it.isNotBlank() }.joinToString(" · ")
        return """<button type="button" class="calls-row" data-call-row="${escape(call.id)}">${avatar(call, "")}<span class="calls-row-main"><strong>${escape(call.callerName.ifBlank { call.callerPhone.ifBlank { "Number withheld" } })}</strong><small class="${if (missed) "missed" else ""}">${escape(detail)}</small></span><span class="calls-row-side"><small>${escape(time(call.startedAtMillis))}</small>${pill(call)}</span></button>"""
    }

    private fun pill(call: WebSupportCall) = when {
        call.linkedTicketId.isNotBlank() -> "<span class=\"calls-pill linked\">${escape(call.linkedTicketNumber.ifBlank { "Ticket" })}</span>"
        call.personal -> "<span class=\"calls-pill personal\">Personal</span>"
        else -> "<span class=\"calls-pill open\">No ticket</span>"
    }

    private fun avatar(call: WebSupportCall, tone: String) =
        "<span class=\"calls-avatar $tone\">${escape(call.callerPhone.filter(Char::isDigit).takeLast(2).ifBlank { "?" })}</span>"

    // --- Detail (Call Log App CallDetailScreen) + actions -------------------------------------

    private fun openDetail(call: WebSupportCall) {
        val answered = CallLogPolicy.isAnswered(call.outcome)
        val tone = if (answered) "answered" else "missed"
        val actions = when {
            call.linkedTicketId.isNotBlank() -> "<button id=\"call-open-ticket\" class=\"btn btn-primary\"><span class=\"material-symbols-rounded\">confirmation_number</span>Open ${escape(call.linkedTicketNumber.ifBlank { "ticket" })}</button>"
            call.personal -> "<button id=\"call-not-personal\" class=\"btn\">Not personal</button>"
            else -> "<button id=\"call-personal\" class=\"btn\">Mark as personal</button>" +
                "<button id=\"call-create-ticket\" class=\"btn btn-primary\"><span class=\"material-symbols-rounded\">add</span>Create ticket</button>"
        } + (if (call.callerPhone.isBlank() || call.linkedTicketId.isNotBlank()) "" else "<button id=\"call-spam\" class=\"btn\"><span class=\"material-symbols-rounded\">report</span>Mark as spam</button>") +
            if (call.callerPhone.isBlank()) "" else "<button id=\"call-assign-name\" class=\"btn\"><span class=\"material-symbols-rounded\">badge</span>${if (call.callerName.isBlank()) "Assign name" else "Edit name"}</button>"
        modal("Call details", """
          <div class="call-detail">
            ${avatar(call, "$tone lg")}
            <h2>${escape(call.callerName.ifBlank { call.callerPhone.ifBlank { "Number withheld" } })}</h2>
            ${if (call.callerName.isNotBlank() && call.callerPhone.isNotBlank()) "<p class=\"call-detail-number\">${escape(call.callerPhone)}</p>" else ""}
            <span class="calls-outcome $tone">${if (call.direction == "outgoing") "Outgoing · " else ""}${escape(CallLogPolicy.outcomeLabel(call.outcome))}</span>
            <div class="call-heroes"><div><strong>${escape(time(call.startedAtMillis))}</strong><span>Time</span></div><div><strong>${if (answered) CallLogPolicy.shortDuration(call.durationSeconds ?: 0) else "–"}</strong><span>Talk time</span></div><div><strong>${escape(localDate(Date(call.startedAtMillis), js("({day:'numeric',month:'short'})")))}</strong><span>Day</span></div></div>
            <div class="detail-grid">
              <div class="detail"><label>Taken by</label><div>${escape(call.receivedByName.ifBlank { "Support user" })}</div></div>
              <div class="detail"><label>Phone</label><div>${escape(call.deviceName.ifBlank { "Unknown device" })}</div></div>
              <div class="detail"><label>Date</label><div>${escape(formatDate(call.startedAt))}</div></div>
              <div class="detail"><label>Ticket</label><div>${escape(when { call.linkedTicketId.isNotBlank() -> call.linkedTicketNumber.ifBlank { "Linked" }; call.personal -> "Personal call"; else -> "None yet" })}</div></div>
            </div>
          </div>
        """.trimIndent(), actions)
        click("call-open-ticket") { closeModal(); WebApp.navigate("tickets"); TicketsScreen.openPath(call.linkedTicketPath) }
        click("call-personal") { setPersonal(call.id, true); closeModal() }
        click("call-not-personal") { setPersonal(call.id, false); closeModal() }
        click("call-spam") { markSpam(call.id); closeModal() }
        click("call-create-ticket") { closeModal(); TicketsScreen.createFromCall(call) }
        click("call-assign-name") { assignName(call) }
    }

    /** Names the number (customerCalls) and every call from it; the server does both writes. */
    private fun assignName(call: WebSupportCall) {
        modal(if (call.callerName.isBlank()) "Assign name" else "Edit name",
            "<div class=\"field\"><label for=\"call-name\">Name for ${escape(call.callerPhone)}</label><input id=\"call-name\" class=\"input\" maxlength=\"160\" placeholder=\"e.g. Suleiman Reddy\" value=\"${escape(call.callerName)}\"></div>",
            "<button id=\"call-name-cancel\" class=\"btn\">Cancel</button><button id=\"call-name-save\" class=\"btn btn-primary\">Save</button>")
        click("call-name-cancel") { closeModal() }
        click("call-name-save") {
            val name = input("call-name")
            if (name.isBlank()) { toast("Enter a name.", true); return@click }
            promise(FirebaseGateway.callable("nameCaller", json("callId" to call.id, "name" to name)), { closeModal(); toast("Saved as $name") })
        }
    }

    // --- Helpers ------------------------------------------------------------------------------

    private fun segmented(id: String, options: List<String>, selected: Int) =
        "<div class=\"segmented\" id=\"$id\" role=\"group\">" + options.mapIndexed { i, o -> "<button type=\"button\" data-seg=\"$i\"${if (i == selected) " class=\"active\" aria-pressed=\"true\"" else " aria-pressed=\"false\""}>${escape(o)}</button>" }.joinToString("") + "</div>"

    private fun bindSegmented(id: String, onPick: (Int) -> Unit) {
        val buttons = element(id)?.querySelectorAll("[data-seg]") ?: return
        for (i in 0 until buttons.length) buttons.item(i)?.addEventListener("click", { e ->
            val node = e.currentTarget as HTMLElement
            if (!node.classList.contains("active")) onPick(node.getAttribute("data-seg")!!.toInt())
        })
    }

    private fun startOfToday(): Double = Date(Date().getFullYear(), Date().getMonth(), Date().getDate()).getTime()
    private fun daysAgo(millis: Double): Int {
        val d = Date(millis)
        return kotlin.math.round((startOfToday() - Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / DAY).toInt()
    }
    private fun dateFor(daysAgo: Int): Date { val t = Date(); return Date(t.getFullYear(), t.getMonth(), t.getDate() - daysAgo) }
    private fun dayName(daysAgo: Int, width: String): String { val options: dynamic = js("({})"); options.weekday = width; return localDate(dateFor(daysAgo), options) }
    private fun dayOfMonth(daysAgo: Int) = dateFor(daysAgo).getDate().toString()
    private fun dayLabel(daysAgo: Int) = when (daysAgo) {
        0 -> "Today"; 1 -> "Yesterday"
        else -> localDate(dateFor(daysAgo), js("({weekday:'long',day:'numeric',month:'short'})"))
    }
    private fun time(millis: Double): String = Date(millis).asDynamic().toLocaleTimeString(undefined, js("({hour:'2-digit',minute:'2-digit'})")) as String
    private fun localDate(date: Date, options: dynamic): String = date.asDynamic().toLocaleDateString(undefined, options) as String
}
